/**
 * 导入预览里的「**列出这次要导入的字**」（迁移 0018，2026-10-19；当天第二版）。
 *
 * ## 用户原话（验收标准）
 *
 * > "**列出这次要导入的字**（用与「浏览与编辑」同一份 `HanziCard` 网格组件，
 * >  手机 3 / 桌面 8 列）……**重复出现的字 → 底色标出**……**不显示拼音**……"
 *
 * 2026-10-19 用户实测后又提了两件事（本文件这一版就是照这两句改的）：
 *
 * > "你现在预览之后，如果出现多音字，你的方法是在**下面单独列一块**给字选择读音。
 * >  目前体验是：**点击那个字之后，就弹出一个浮窗**给他来选择，就不用跑到下面去……
 * >  如果没有看到的话，还不知道下面有选择的呢。**最好就地或者弹窗，有个窗来选择**。"
 *
 * > "我看到下面还有一个空，有一块是『重复出现的位置，一个字』……**如果是多音字的话，
 * >  这边就没必要显示了**……**这里可以用来显示那些不是多音字、还有重复出现的字。
 * >  如果没有，那就不用显示。**"
 *
 * ## 三件事分开画
 *
 *   1. **字卡网格**（`HANZI_GRID_CLASS` + `HanziCard`，与浏览页同一份）：
 *      每个**位置**一张卡（同一个字出现两次就是两张卡）；重复出现 = 浅玫瑰底色；
 *      点一张卡 → **立刻弹出 `ImportReadingModal`**（读音选择只在浮窗里，下面不再有一块）；
 *   2. **「重复出现的位置」**：只列**重复出现且技术上不是多音字**的字
 *      （判据与理由见 `nonPolyphonicRepeatedHints()`）；没有这类字时**整块不渲染**；
 *   3. **三道校验**：没标音 / 同字同音 —— 红字列出，`ok=false` 时禁用提交。
 *
 * ## 浮窗里的「这个字还出现在」→ **点一下就跳过去**（2026-10-20）
 *
 * 用户原话：
 *
 * > "我找到一个字并改了它的读音，之后**想找它另外一个的时候，就要不断地往下翻**……
 * >  在**弹出的浮窗里面加一个功能，直接定位到另外一个字**。"
 *
 * 浮窗里那一行不再只是文字：每一项是按钮（`HanziJumpLinks`），点了就
 * **关浮窗 → 滚到那张卡 + 高亮 2 秒 + 焦点落到卡上** —— **到此为止**。
 * 滚动 / 高亮 / 聚焦 / 取消全部在 `hanziJump.ts` 的 `runHanziJump()` / `useHanziJump()`
 * ——**与「浏览与编辑」同一份实现**，本文件只负责接线。
 *
 * ## ⚠️ 2026-10-20：跳过去**不再自动打开**那个位置的读音浮窗
 *
 * 用户原话：
 *
 * > "跳转过后呢，**你不要自动点击那个跳转的字**，然后你一点的话，它就自动弹出那个
 * >  浮窗来了。……你**高亮 2 秒就行了，你不要把它的弹窗点开**呀。……**两边都是一样**。"
 *
 * 所以 `jumpToOther()` 只做两件事：**先关窗**（`Modal` 开着时 `body` 是
 * `overflow: hidden`，不关滚不动）→ `jump.jumpTo(key)`。看到 ring 之后由**用户自己
 * 点那张卡**才会弹 `ImportReadingModal`（点卡那条路径一个字都没改）。
 *
 * ## ⚠️ 2026-10-20（第三轮）：底色三态 + **显示拼音**
 *
 * 用户原话（本文件这一版就是照这三句改的）：
 *
 * > "录入汉字这里，有些多音字要标音。一开始没标音的时候，它是**玫瑰色的底色**。
 * >  你再改一下：**就当我标音之后呢，把它底色变成另外一种颜色**，表示它**已经标音了**。"
 *
 * > "对于**其他不是多音字的字**，你就按照你们**库里面的音**，直接给它**标一个拼音**……
 * >  我会**逐字去核对他的拼音对不对**。"
 *
 * 于是：
 *
 *   1. **字卡显示拼音**（`showPinyin` 用默认的 `true`）：标过音的位置显示**这个位置
 *      标定的音**（`position.reading`）；没标过的显示**库里的默认读音**
 *      （`position.effectiveReading` = `pinyinState(char, null).auto`，即内置字表的
 *      默认音）；空读音显示「（没有读音）」。
 *   2. **底色三态**（详见 `HanziCard`）：重复 + 未标音 = 浅玫瑰；重复 + 已标音 = **浅绿**；
 *      只出现一次 = 白。
 *
 * ⚠️ 更早那句"拼音你先不标在导入结果那里"**以本次为准**（用户明确要逐字核对拼音）。
 * ⚠️ 重复用**浅玫瑰**、已标音用**浅绿**，与浏览页"已确认多音字"的琥珀色区分开。
 * ⚠️ 选读音的状态机（点卡开窗 / 选音关窗+重新预览 / 取消不改）在
 *    `importPreviewReadingPicker.ts` —— 本文件只负责接线。
 *
 * ## ⚠️ 2026-10-20（第四轮）：字卡**按「粘贴时的行」分组**
 *
 * 用户原话（本文件这一版就是照它改的）：
 *
 * > "……**能否按照我每一行录入的字来排列**，按行来排列那些字？你现在是**连续排列**，
 * >  这样连续排列的情况下，**我就不知道录入时这个字属于第几行了**。正确的排列方式是：
 * >  ① **一行的字就顺着排**；② **第二行就换行**……这样我核对的时候，
 * >  可以**一行一行地核对**。"
 *
 * 所以：
 *
 *   · 一行 = 一块（`<section data-line-group>`），块标题逐字 `第 3 行（7 个字）`；
 *     多张字表混在一起时标题带表名：`识字表 · 第 3 行（7 个字）`；
 *   · 块内仍用**同一个** `HANZI_GRID_CLASS`（手机 3 / 小屏 6 / 桌面 8 列），
 *     一行字多到放不下时**在同一块里自动折行** —— 块标题已经把"这是哪一行"标住了；
 *   · 顺序：块按行号升序、块内按 `indexInLine`（= `sort_order` = 粘贴顺序）；
 *   · 分组与标题的纯逻辑在 `importPreviewLineGroups.ts`（有单测）。
 *
 * ⚠️ **一个字都没改**：底色三态（玫瑰 / 浅绿 / 白）、「已标音」、拼音、点卡开浮窗、
 *    跳转（`cardRef` + `highlighted`，key 仍是 `hanziPositionKey(line, indexInLine)`）
 *    —— 只是把卡片放进"行块"里，跳转的登记键 / 高亮判据完全没变。
 *
 * ## ⚠️ 2026-10-21：**朗读控制条与四段说明已删**（用户明确要求"全删，不保留"）
 *
 * 用户原话：
 *
 * > "『按书录入』模块下面有太多的说明文字块了，眼花缭乱，要清理一下……
 * >  **朗读文字那一块不需要了**，去掉（现在不用逐字核对，这个功能就没用了）。"
 *
 * 所以预览顶上只剩：标题一行 + 字卡网格（+ 校验失败时的红字列表）。
 * `ImportSpeechBar` / `useImportPreviewSpeech` 这些文件**保持存在**（红线：不删
 * 非本轮创建的文件），但本文件**不再引用**它们 —— 由源码守卫钉住，不许被加回来。
 *
 * ## ⚠️ 2026-10-21（本轮）：**待确认那道的逐条提示已删，只留汇总一句**
 *
 * 用户原话：
 *
 * > "02 和 03 图中**上面一行一行的提示和下面一段的提示是重复**，
 * >  **去掉一行一行的提示，保留下面红框的那段提示**。"
 *
 * 所以「有 N 个字的读音需要你确认（不能保存）」这块里：
 *   · 标题照旧；
 *   · **逐条列表**（`・「落」（第 1 行第 3 个字）的读音「luò」在文件里标了「?」（不确定）
 *     —— 点它确认这个音，才能保存。` × 46 条）**整段删掉**；
 *   · `readings.needsConfirmSummary`（"有 46 个字的读音需要你确认：落（…）、…，
 *     确认之后才能保存。"）**逐字保留**，就是用户红框圈住的那一段。
 *
 * ⚠️ 删的只是**文字**：`readings.needsConfirm` 数据、`ok` 控制能不能保存
 *    （`readings.ok`）、点字卡确认读音的整条路 —— **一个字都没动**。
 * ⚠️ 本文件的「没标音」与「同字同音」两块**不在本次范围**（用户只点了待确认那 46 条），
 *    原样保留。
 */
import { useMemo, useState } from 'react';
import type { HanziView, ImportReportView } from '../../lib/api/dictation';
import { HANZI_GRID_CLASS, HanziCard } from './HanziCard';
import { ImportReadingModal } from './ImportReadingModal';
import {
  INITIAL_IMPORT_PREVIEW_PICKER,
  nonPolyphonicRepeatedHints,
  reduceImportPreviewPicker,
  type ImportPreviewPickerEvent,
  type ImportPreviewPickerState,
} from './importPreviewReadingPicker';
import { hanziPositionKey, importJumpCards, useHanziJump } from './hanziJump';
import {
  describeImportPreviewCounts,
  groupImportPositionsByLine,
} from './importPreviewLineGroups';

/** 把一个"位置"装成 `HanziCard` 认识的 `HanziView`（只填卡片真正会读的字段）。 */
function positionToHanzi(
  position: NonNullable<ImportReportView['readings']>['positions'][number],
): HanziView {
  return {
    id: `position-${position.line}-${position.indexInLine}`,
    textbookId: '',
    charText: position.charText,
    pinyinAuto: '',
    /**
     * **这个位置的读音兜底**（迁移 0018）。
     *
     * · 没标音时 = **库里的默认读音**：`position.effectiveReading` 由后端算成
     *   `pinyinState(charText, null).auto`（内置字表的主读音，等价于
     *   `availableReadings(charText)[0]`）。
     * · 标了音时卡片优先读下面的 `listingReading`（这个位置标定的音），不读这里。
     * · 空串 = 连默认读音都取不到 ⇒ 卡片显示「（没有读音）」。
     */
    pinyin: position.effectiveReading,
    pinyinOverridden: position.reading !== '',
    pinyinAlternates: [],
    readings: [],
    polyphonic: false,
    hiddenReadings: [],
    gradeCode: null,
    semester: null,
    listings: null,
    listingId: null,
    listingReading: position.reading === '' ? null : position.reading,
    listingSortOrder: position.indexInLine,
    note: '',
    lessonIds: null,
  };
}

export interface ImportListingPreviewProps {
  report: ImportReportView;
  /** 正在重新预览（选了读音之后）。 */
  busy: boolean;
  /** 给这个位置选一个读音（父组件会把它加进预览请求并重新预览）。 */
  onPickReading: (line: number, index: number, reading: string) => void;
}

export function ImportListingPreview({
  report,
  busy,
  onPickReading,
}: ImportListingPreviewProps) {
  const readings = report.readings;
  const [picker, setPicker] = useState<ImportPreviewPickerState>(INITIAL_IMPORT_PREVIEW_PICKER);
  /**
   * 同一个字的不同位置之间跳转（**与「浏览与编辑」同一份实现**，见 `hanziJump.ts`）。
   * 本组件自己实例化一次：预览网格与它的浮窗都在这棵子树里。
   */
  const jump = useHanziJump();

  const positions = readings?.positions ?? [];

  /**
   * 浮窗里"还出现在哪"：同一个字在**别的**位置。
   *
   * ⚠️ 从 `positions` 自己筛，而不是只信 `readings.repeated`：点**只出现一次**的字
   *    也能开窗（统一入口），那时这里就是空数组，浮窗说"没有别的位置了"。
   */
  const selectedPosition = useMemo(() => {
    if (picker.selected === null) return null;
    return (
      positions.find(
        (position) =>
          position.line === picker.selected?.line &&
          position.indexInLine === picker.selected?.indexInLine,
      ) ?? null
    );
  }, [positions, picker.selected]);

  /**
   * 浮窗里「这个字还出现在」的**可点列表**（每项带"已标 / 未标音"）。
   *
   * ⚠️ 从 `positions` 自己筛（`importJumpCards`），而不是只信后端的 `readings.repeated`：
   *    点**只出现一次**的字也能开窗（统一入口），那时这里就是空数组，
   *    浮窗说"没有别的位置了（这次只出现这一处）"。
   */
  const otherCards = useMemo(
    () => importJumpCards(positions, selectedPosition),
    [positions, selectedPosition],
  );

  /**
   * 「重复出现的位置」只留**非多音字**；没有就整块不渲染（连标题都不出现）。
   * ⚠️ 在早返回之前算（Hook 顺序不能变）。
   */
  const typoHints = useMemo(
    () => nonPolyphonicRepeatedHints(readings?.repeated ?? []),
    [readings],
  );

  /**
   * **按「粘贴时的行」分组**（2026-10-20）：一行 = 一块，行号升序、组内按粘贴顺序。
   * 同号不同表也是两块（`识字表 · 第 3 行` / `写字表 · 第 3 行`）。
   * ⚠️ 同样在早返回之前算（Hook 顺序不能变）。
   */
  const lineGroups = useMemo(() => groupImportPositionsByLine(positions), [positions]);

  if (readings === null) {
    return (
      <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        这次没拿到「每个位置标了什么音」的校验结果（后端没返回 <code>readings</code>）——
        <strong>不能当作校验通过</strong>：请刷新重试，或先不要提交。
      </p>
    );
  }

  /**
   * 事件 → 状态 + 副作用。只有 `pickReading` 会让父组件重新预览；
   * 「取消」与「开窗」都**一个请求都不发**（见 `importPreviewReadingPicker.ts`）。
   */
  const apply = (event: ImportPreviewPickerEvent) => {
    const step = reduceImportPreviewPicker(picker, event);
    setPicker(step.state);
    if (step.effect.type === 'pickReading') {
      onPickReading(step.effect.line, step.effect.indexInLine, step.effect.reading);
    }
  };

  /**
   * 点浮窗里「这个字还出现在」的某一项 → **跳到那张卡**。
   *
   * 顺序是刻意的（用户两条要求都在这儿）：
   *   1. **先关浮窗**：`Modal` 开着时 `body` 是 `overflow: hidden`，不关就滚不动；
   *   2. `jump.jumpTo()` 滚到目标卡 + 高亮 2 秒 + 把焦点落到卡上；
   *   3. **到此为止** —— ⛔ 不打开目标位置的浮窗（用户 2026-10-20 点名不要）；
   *      用户看到 ring 之后自己点那张卡，才会弹出读音浮窗。
   */
  const jumpToOther = (key: string) => {
    apply({ type: 'cancel' });
    jump.jumpTo(key);
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
        <p className="text-sm font-semibold text-slate-800">
          这次要导入的字（{describeImportPreviewCounts(positions, lineGroups.length)}）
        </p>

        {positions.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">这次没有要导入的字（只有词）。</p>
        ) : (
          <div className="mt-3 space-y-4">
            {lineGroups.map((group) => (
              <section
                key={group.key}
                data-line-group={group.line}
                data-list-type={group.listType ?? ''}
                aria-label={group.title}
                className="rounded-xl border border-slate-200 bg-slate-50/60 px-2 py-2"
              >
                <h4 className="mb-1.5 px-0.5 text-sm font-semibold text-slate-800">
                  {group.title}
                </h4>
                <ul className={HANZI_GRID_CLASS}>
                  {group.positions.map((position) => {
                    const key = hanziPositionKey(position.line, position.indexInLine);
                    /**
                     * 高亮只有一种来源（朗读控制条 2026-10-21 已删）：
                     * `jump.activeKey` = 用户刚点了「跳到这个字的另一个位置」（会滚动）。
                     * ⚠️ 高亮是受控的，`HanziCard` 只负责画（见它的文件头）。
                     */
                    return (
                      <HanziCard
                        key={key}
                        item={positionToHanzi(position)}
                        duplicate={position.duplicate}
                        marked={position.reading !== ''}
                        /**
                         * **文件里标了「?」的两态**（2026-10-20 第七轮）：
                         *   · `uncertain` → 浅橙（还要你确认）；
                         *   · `confirmed` → 浅绿（文件标过 `?`、你已经确认）。
                         * ⚠️ `confirmed` 不能只看 `reading !== ''`：那样"确认过的单次字"
                         *    会退回白底，用户看不出自己刚确认过。
                         */
                        uncertain={position.uncertain === true}
                        confirmed={position.wasUncertain === true && position.uncertain !== true}
                        caption={position.label}
                        cardRef={jump.register(key)}
                        highlighted={jump.activeKey === key}
                        onOpen={() => {
                          /** 用户自己点了卡 ⇒ 取消可能还在路上的跳转，别把它抢走。 */
                          jump.cancel();
                          apply({
                            type: 'open',
                            line: position.line,
                            indexInLine: position.indexInLine,
                          });
                        }}
                      />
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {/*
        ---------- 这个位置的读音（**浮窗**，不再在下面单独列一块） ----------
        ⚠️ 用户原话："如果没有看到的话，还不知道下面有选择的呢" —— 所以入口只有
           "点字卡"这一条，浮窗里把字、位置、其它位置、可选读音一次说清。
      */}
      <ImportReadingModal
        position={selectedPosition}
        others={otherCards}
        busy={busy}
        /**
         * **唯一**的确认入口（2026-10-20 第八轮，"先选、再确认"）：
         * 浮窗里点读音只是选中，点「✅ 确认就是「X」」才把**选中的音**交出来
         * —— 读音不变 = 只确认；换了音 = 改读音 + 确认（两种都算确认）。
         */
        onConfirm={(reading) => apply({ type: 'confirm', reading })}
        onClose={() => apply({ type: 'cancel' })}
        onJumpToOther={jumpToOther}
      />

      {/* ---------- 可能是录错了：**只列非多音字**的重复字 ---------- */}
      {typoHints.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
          <p className="text-sm font-semibold text-rose-800">
            这些字重复出现了，而且不是多音字 —— 可能是录错了
          </p>
          <ul className="mt-1 space-y-0.5 text-sm text-rose-900">
            {typoHints.map((hint) => (
              <li key={`${hint.charText}-${hint.first.line}-${hint.first.indexInLine}`}>
                · {hint.message}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-rose-800">
            只是提示，不影响保存 —— 多音字重复是正常的（一个字有多个读音），所以这里不列它们。
          </p>
        </div>
      )}

      {/* ---------- 三道校验：待确认（`?`）/ 没标音 / 同字同音 ---------- */}
      {/*
        ⚠️ 2026-10-20 第七轮新增的一道：文件里标了 `?`、用户还没确认的位置
        **等同"没标音"——不许保存**。文案是用户点名的
        「有 N 个字的读音需要你确认」，下面逐字给出位置。

        ⚠️ 2026-10-21（本轮）：**一行一行的逐条提示已删** —— 用户原话：
        "02 和 03 图中上面一行一行的提示和下面一段的提示是重复，去掉一行一行的提示，
         保留下面红框的那段提示。"
        所以这块只剩**标题 + 汇总那一句**（"有 N 个字的读音需要你确认：落（…）、…，
        确认之后才能保存。"），逐条列表不再渲染。
        ⚠️ `readings.needsConfirm` 数据照旧返回、`ok` 照旧控制能不能保存 ——
           删的只是那 46 行字，校验一个字都没动。
      */}
      {(readings.needsConfirm ?? []).length > 0 && (
        <div className="rounded-xl border border-orange-300 bg-orange-50 px-3 py-2">
          <p className="text-xs text-orange-800">
            {readings.needsConfirmSummary !== undefined && readings.needsConfirmSummary !== ''
              ? readings.needsConfirmSummary
              : `有 ${readings.needsConfirmChars?.length ?? readings.needsConfirm?.length ?? 0} 个字的读音需要你确认，确认之后才能保存。`}
          </p>
        </div>
      )}

      {readings.missing.length > 0 && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2">
          <p className="text-sm font-semibold text-rose-800">
            有 {readings.missingChars.length} 个字没有标音（不能保存）
          </p>
          <ul className="mt-1 space-y-0.5 text-sm text-rose-900">
            {readings.missing.map((issue) => (
              <li key={`missing-${issue.line}-${issue.indexInLine}`}>· {issue.message}</li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-rose-800">{readings.missingSummary}</p>
        </div>
      )}

      {readings.conflicts.length > 0 && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2">
          <p className="text-sm font-semibold text-rose-800">
            「这个音已经存在了，不能再录入了」（{readings.conflicts.length} 处，不能保存）
          </p>
          <ul className="mt-1 space-y-0.5 text-sm text-rose-900">
            {readings.conflicts.map((issue) => (
              <li key={`conflict-${issue.line}-${issue.indexInLine}-${issue.reading}`}>
                · {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {readings.ok && positions.length > 0 && (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          ✅ 三道校验都过了：重复出现的字都标了音，也没有「同字同音」—— 可以保存。
        </p>
      )}
    </div>
  );
}
